<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import conversationAPI from 'dashboard/api/inbox/conversation';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  selectedConversationId: {
    type: [Number, String, null],
    default: null,
  },
});

const emit = defineEmits(['update:show', 'select']);

const { t } = useI18n();

const conversations = ref([]);
const loading = ref(false);
const search = ref('');

const closeModal = () => {
  emit('update:show', false);
};

const getConversationName = conversation => {
  const sender = conversation?.meta?.sender || conversation?.contact || {};
  return (
    sender.name ||
    sender.email ||
    t('KANBAN.FORM.CONVERSATION.RELATED_FALLBACK', {
      id: conversation?.id || '?',
    })
  );
};

const getConversationPreview = conversation => {
  const lastMessage =
    conversation?.messages?.[0]?.content ||
    conversation?.last_non_activity_message?.content ||
    '';

  return lastMessage.trim();
};

const getConversationSummary = conversation => {
  return `#${conversation.id} - ${getConversationName(conversation)}`;
};

const fetchConversations = async () => {
  try {
    loading.value = true;
    const response = await conversationAPI.get({});
    conversations.value = response.data?.data?.payload || [];
  } catch (error) {
    conversations.value = [];
  } finally {
    loading.value = false;
  }
};

const filteredConversations = computed(() => {
  const query = search.value.trim().toLowerCase();

  if (!query) {
    return conversations.value;
  }

  return conversations.value.filter(conversation => {
    const content = [
      conversation.id,
      getConversationName(conversation),
      getConversationPreview(conversation),
    ]
      .join(' ')
      .toLowerCase();

    return content.includes(query);
  });
});

const handleSelect = conversation => {
  emit('select', conversation);
  closeModal();
};

watch(
  () => props.show,
  async isVisible => {
    if (!isVisible) return;

    search.value = '';
    await fetchConversations();
  }
);
</script>

<template>
  <Modal
    :show="show"
    :on-close="closeModal"
    show-close-button
    size="medium"
  >
    <div class="p-4 space-y-4">
      <div>
        <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100">
          {{ t('KANBAN.FORM.NOTES.SELECT_CONVERSATION') }}
        </h3>
      </div>

      <input
        v-model="search"
        type="text"
        class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
        :placeholder="t('KANBAN.FORM.CONVERSATION.SEARCH_PLACEHOLDER')"
      />

      <div
        class="max-h-96 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700"
      >
        <div
          v-if="loading"
          class="px-4 py-8 text-sm text-center text-slate-500 dark:text-slate-400"
        >
          {{ t('KANBAN.LOADING') }}
        </div>

        <template v-else>
          <button
            v-for="conversation in filteredConversations"
            :key="conversation.id"
            type="button"
            class="w-full px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            :class="{
              'bg-woot-50 dark:bg-woot-900/20':
                String(selectedConversationId) === String(conversation.id),
            }"
            @click="handleSelect(conversation)"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <p
                  class="text-sm font-medium text-slate-900 dark:text-slate-100 truncate"
                >
                  {{ getConversationSummary(conversation) }}
                </p>
                <p
                  v-if="getConversationPreview(conversation)"
                  class="mt-1 text-xs text-slate-500 dark:text-slate-400 truncate"
                >
                  {{ getConversationPreview(conversation) }}
                </p>
              </div>
              <span
                class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                :class="{
                  'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300':
                    conversation.status === 'open',
                  'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300':
                    conversation.status === 'pending',
                  'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300':
                    conversation.status === 'resolved',
                }"
              >
                {{ conversation.status }}
              </span>
            </div>
          </button>
        </template>

        <div
          v-if="!loading && filteredConversations.length === 0"
          class="px-4 py-8 text-sm text-center text-slate-500 dark:text-slate-400"
        >
          {{ t('KANBAN.FORM.NOTES.NO_CONVERSATIONS') }}
        </div>
      </div>

      <div class="flex justify-end">
        <button
          type="button"
          class="px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          @click="closeModal"
        >
          {{ t('KANBAN.CANCEL') }}
        </button>
      </div>
    </div>
  </Modal>
</template>
